import { httpClient } from '../http-client'
import {
    checkToken,
    excelDateToJSDate,
    handleError,
    wisePriceRateLimiter,
} from '../utils'
import { demoTickers } from '../constants'

/**
 * Get live and historical price data.
 * @customfunction
 * @helpurl https://blog.wisesheets.io/the-ultimate-guide-to-using-wisesheets/
 * @param {string[][]} symbols Company symbol(s).
 * @param {string[][]} parameters Data point(s) to retrieve.
 * @param {number} [numDaysToEnd] Number of days to end of date (optional).
 * @param {string} [startDate] Start date ie. 2018-03-12 (optional).
 * @param {string} [endDate] End date ie. 2019-03-12 (optional).
 * @returns {any[][]} Data or some error.
 */
// @ts-ignore
async function WISEPRICE(
    symbols,
    parameters,
    numDaysToEnd,
    startDate,
    endDate
) {
    if (symbols.flat().length > 5001) {
        return [['Please select less than 5,000 tickers to get data.']]
    }
    // maintaining original structure including empty symbols
    const filteredSymbols = symbols

    const symbolsToCheck = symbols
        .flat()
        .filter((symbol: string) => symbol.trim() !== '')

    if (symbolsToCheck.length === 0) {
        return [['Please enter at least one valid symbol.']]
    }

    const { error, subscriptionType } = checkToken(symbolsToCheck, demoTickers)
    if (error) {
        return error
    }

    try {
        // Batch requests in groups of 1000 tickers using filtered symbols
        const BATCH_SIZE = 1000

        // Calculate weight (number of batches) for rate limiting
        const weight = Math.ceil(symbolsToCheck.length / BATCH_SIZE)

        const rateLimitError = await wisePriceRateLimiter.checkAndApplyLimit(
            subscriptionType,
            weight
        )
        if (rateLimitError) {
            return [[rateLimitError]]
        }

        // If symbols length is within batch size, make single request
        if (filteredSymbols.flat().length <= BATCH_SIZE) {
            const data = await httpClient.post(
                '/api/private/functions/wise-price',
                {
                    symbols: filteredSymbols,
                    parameters,
                    numDaysToEnd,
                    startDate:
                        startDate == null ? null : excelDateToJSDate(startDate),
                    endDate:
                        endDate == null ? null : excelDateToJSDate(endDate),
                    platform: 'EXCEL',
                }
            )
            return data.data
        }

        // Create batches from filteredSymbols maintaining 2D array structure
        const batches: string[][][] = []
        let currentBatch: string[][] = []
        let currentCount = 0

        for (const row of filteredSymbols) {
            for (const symbol of row) {
                if (currentCount >= BATCH_SIZE) {
                    batches.push(currentBatch)
                    currentBatch = []
                    currentCount = 0
                }
                currentBatch.push([symbol])
                currentCount++
            }
        }

        // Push the last batch if it has items
        if (currentBatch.length > 0) {
            batches.push(currentBatch)
        }

        // Process all batches in parallel, skipping all-empty batches
        const batchPromises = batches.map((batch) => {
            if (batch.flat().every((s: string) => s.trim() === '')) {
                return Promise.resolve(null)
            }
            return httpClient.post('/api/private/functions/wise-price', {
                symbols: batch,
                parameters,
                numDaysToEnd,
                startDate:
                    startDate == null ? null : excelDateToJSDate(startDate),
                endDate: endDate == null ? null : excelDateToJSDate(endDate),
                platform: 'EXCEL',
            })
        })

        // Wait for all requests to complete (order is maintained)
        const batchResponses = await Promise.all(batchPromises)

        // Combine results from all batches
        const results: any[][] = []
        for (const response of batchResponses) {
            if (response === null) continue
            // Each batch returns a 2D array, concatenate rows
            for (const row of response.data) {
                results.push(row)
            }
        }

        return results
    } catch (e) {
        return handleError(e)
    }
}

// Export for FMP wrapper functions
export { WISEPRICE }

CustomFunctions.associate("WISEPRICE", WISEPRICE);